import { BalanceType } from '@/entities/balances'
import { Currencies } from '@/entities/curency'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { BaseButton, BaseCard} from '@/shared/components'
import { formatCurrency } from '@/shared/lib/currency'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { RootState } from '@/store/store'
import { CircleArrowRight, CircleArrowUp, CirclePlus, Snowflake } from 'lucide-react'

import { useSelector } from 'react-redux'

type BalanceCardProps = {
  onTopup: () => void
  onWithdraw: () => void
  onActions: () => void
}

export const BalanceCard = ({ onTopup, onWithdraw, onActions }: BalanceCardProps) => {
  const { balances } = useSelector((state: RootState) => state.balances)
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)

  const userBalance = balances.find((b) => b.balanceType === BalanceType.User)?.amount ?? 0
  const userBalanceFrozen = balances.find((b) => b.balanceType === BalanceType.User)?.frozen ?? 0

  const depositBalance = balances.find((b) => b.balanceType === BalanceType.Deposit)?.amount ?? 0
  const depositBalanceFrozen =
    balances.find((b) => b.balanceType === BalanceType.Deposit)?.frozen ?? 0

  const insuranceBalance =
    balances.find((b) => b.balanceType === BalanceType.Insurance)?.amount ?? 0
  const insuranceBalanceFrozen =
    balances.find((b) => b.balanceType === BalanceType.Insurance)?.frozen ?? 0

  const userBalanceFmt = formatCurrency(userBalance, Currencies.USDT)
  const userBalanceFrozenFmt = formatCurrency(userBalanceFrozen, Currencies.USDT)

  const depositBalanceFmt = formatCurrency(depositBalance, Currencies.USDT)
  const depositBalanceFrozenFmt = formatCurrency(depositBalanceFrozen, Currencies.USDT)

  const insuranceBalanceFmt = formatCurrency(insuranceBalance, Currencies.USDT)
  const insuranceBalanceFrozenFmt = formatCurrency(insuranceBalanceFrozen, Currencies.USDT)

  if (userRole === Roles.Merchant) {
    return (
      <BaseCard>
        <div className="flex flex-row md:flex-col justify-between gap-4 md:gap-6">
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-2">
              <p className="text-text-secondary text-sm leading-[18px]">Основной баланс</p>
            </div>
            <p className="text-main-black dark:text-white text-lg">{userBalanceFmt}</p>
            <div className="mb-2 flex items-center gap-2 dark:text-primary-dark-disabled text-primary-disabled">
              <Snowflake className="w-4 h-4" />
              <span>{userBalanceFrozenFmt}</span>
            </div>
            <BaseButton
              variant="transparent"
              padding="none"
              className="disabled:opacity-50 !p-0 text-primary hover:text-primary-hover dark:hover:text-primary-dark-hover dark:text-primary-dark"
              onClick={onActions}
              iconPosition="right"
              icon={<CircleArrowRight width={24} height={24} />}
            >
              Перейти
            </BaseButton>
          </div>
          <div className="flex flex-col gap-y-1">
            <p className="text-text-secondary text-sm">Страховой баланс</p>
            <p className="text-main-black dark:text-white text-lg">{insuranceBalanceFmt}</p>
            <div className="mb-2 flex items-center gap-2 dark:text-primary-dark-disabled text-primary-disabled">
              <Snowflake className="w-4 h-4" />
              <span>{insuranceBalanceFrozenFmt}</span>
            </div>
          </div>
        </div>
      </BaseCard>
    )
  }

  // Для роли Trader и остальных показываем полный интерфейс
  return (
    <BaseCard>
      <div className="flex flex-row md:flex-col justify-between gap-4 md:gap-6">
        <div className="flex flex-col gap-y-1">
          <div className="flex items-center gap-2">
            <p className="text-text-secondary text-sm leading-[18px]">Баланс депозита</p>
          </div>
          <p className="text-main-black dark:text-white text-lg">{depositBalanceFmt}</p>
          <div className="mb-2 flex items-center gap-2 dark:text-primary-dark-disabled text-primary-disabled">
            <Snowflake className="w-4 h-4" />
            <span>{depositBalanceFrozenFmt}</span>
          </div>
          <BaseButton
            variant="transparent"
            padding="none"
            className="disabled:opacity-50 !p-0 text-primary hover:text-primary-hover dark:hover:text-primary-dark-hover dark:text-primary-dark"
            onClick={onTopup}
            iconPosition="right"
            icon={<CircleArrowUp width={24} height={24} />}
          >
            Пополнить
          </BaseButton>
        </div>
        <div className="flex flex-col gap-y-1">
          <p className="text-text-secondary text-sm">Баланс вознаграждения</p>
          <p className="text-main-black dark:text-white text-lg">{userBalanceFmt}</p>
          <div className="mb-2 flex items-center gap-2 dark:text-primary-dark-disabled text-primary-disabled">
            <Snowflake className="w-4 h-4" />
            <span>{userBalanceFrozenFmt}</span>
          </div>
          <BaseButton
            variant="transparent"
            padding="none"
            className="disabled:opacity-50 !p-0 text-primary hover:text-primary-hover dark:hover:text-primary-dark-hover dark:text-primary-dark"
            onClick={onWithdraw}
            iconPosition="right"
            icon={<CirclePlus width={24} height={24} />}
          >
            Вывести
          </BaseButton>
        </div>
      </div>
    </BaseCard>
  )
}
